import { PriceObjectDto, PriceObjectDtoSchema } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { ProgramTrialSubscriptionDto } from "@modules/common/trialSubscription/gateway/trialSubscriptionApiDto"
import { convertImageFromDto, HUImageDtoSchema } from "@modules/dtos/imageDto"
import { CoachDtoSchema, convertCoachFromDto, convertUserFromDto } from "@modules/profile/profileDtos"
import { ProgramBundleLightDtoSchema, ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleLightEntity } from "@modules/programBundle/entities/programBundleEntity"
import {
  ItemProgramInfinite,
  ProgramInfinite,
  ProgramInfiniteDraft,
  ProgramInfiniteStripeSubscriptionInfos,
  ProgramInfiniteTypeOf,
} from "@modules/programs/programInfiniteTypes"
import {
  ProgramContentTypeOf,
  ProgramPrice,
  ProgramSubscriptionStatus,
  ProgramVisibility,
  SharedCoachStatus,
} from "@modules/programs/programTypes"
import {
  convertProgramPaymentFromDto,
  convertProgramPriceFromDto,
  ProgramPaymentDto,
} from "@modules/programs/services/programDto"
import { ProgramInfiniteSessionDtoSchema } from "@modules/programSession/services/programSessionDto"
import { SharedCoachDtoSchema } from "@modules/sharedCoach/sharedCoachDtos"
import { convertToEnum } from "@modules/utils/enum"
import { z } from "zod"

export const ItemProgramInfiniteDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  visibility: z.nativeEnum(ProgramVisibility),
  subscribed: z.boolean(),
  image: HUImageDtoSchema,
  owner: CoachDtoSchema,
  sessions: z.array(ProgramInfiniteSessionDtoSchema).optional(),
  subscriptionCount: z.number(),
  defaultHideSessionContent: z.boolean(),
  scoreboardVisibility: z.boolean(),
  entranceCheckEnabled: z.boolean(),
  price: PriceObjectDtoSchema.nullable(),
  trialDuration: z.number(),
  type: z.string(),
  subscriptionStatus: z.nativeEnum(ProgramSubscriptionStatus),
  invitationLink: z.string(),
  isEligibleToTrial: z.boolean(),
  sharedCoaches: z.array(SharedCoachDtoSchema).optional(),
  sharedCoachStatus: z.nativeEnum(SharedCoachStatus).optional(),
  obtainableOnlyInBundle: z.boolean().nullable(),
})

export type ItemProgramInfiniteDto = z.infer<typeof ItemProgramInfiniteDtoSchema>

export const ProgramInfiniteDtoSchema = ItemProgramInfiniteDtoSchema.extend({
  description: z.string(),
  programBundle: ProgramBundleLightDtoSchema.nullable(),
})

export type ProgramInfiniteDto = z.infer<typeof ProgramInfiniteDtoSchema>

export type ProgramBundleLightDto = z.infer<typeof ProgramBundleLightDtoSchema>

export type ProgramInfiniteDraftDto = {
  name: string
  description: string
  visibility: ProgramVisibility
  image?: string
  defaultHideSessionContent: boolean
  scoreboardVisibility?: boolean
  entranceCheckEnabled: boolean
  sharedCoaches?: string[]
  obtainableOnlyInBundle: boolean
}

export function convertItemProgramInfiniteFromDto(dto: ItemProgramInfiniteDto): ItemProgramInfinite {
  return {
    _programType: ProgramInfiniteTypeOf,
    id: dto.id,
    title: dto.name,
    visibility: dto.visibility,
    image: convertImageFromDto(dto.image),
    author: convertCoachFromDto(dto.owner),
    subscriptionCount: dto.subscriptionCount,
    defaultHideSessionContent: dto.defaultHideSessionContent,
    hideScoreboard: !dto.scoreboardVisibility,
    entranceCheckEnabled: dto.entranceCheckEnabled,
    price: dto.price as ProgramPrice,
    subscriptionStatus: dto.subscriptionStatus,
    subscribed: dto.subscribed,
    invitationLink: dto.invitationLink,
    trialDuration: dto.trialDuration,
    _type: ProgramContentTypeOf,
    isEligibleToTrial: dto.isEligibleToTrial,
    sharedCoaches: dto.sharedCoaches?.map((sharedCoach) => ({
      ...sharedCoach,
      user: convertUserFromDto(sharedCoach.user),
    })),
    sharedCoachStatus: dto.sharedCoachStatus,
    obtainableOnlyInBundle: dto.obtainableOnlyInBundle ?? false,
  }
}

export function convertProgramInfiniteFromDto(dto: ProgramInfiniteDto): ProgramInfinite {
  return {
    ...convertItemProgramInfiniteFromDto(dto),
    description: dto.description,
    programBundle: dto.programBundle
      ? ProgramBundleLightEntity.fromDto(dto.programBundle, ProgramBundleType.VIRTUAL)
      : undefined,
  }
}

export function convertProgramInfiniteDraftToDto(draft: ProgramInfiniteDraft): ProgramInfiniteDraftDto {
  return {
    name: draft.name,
    description: draft.description,
    visibility: draft.visibility,
    image: draft.image?.path,
    defaultHideSessionContent: draft.defaultHideSessionContent,
    scoreboardVisibility: !draft.hideScoreboard,
    entranceCheckEnabled: draft.entranceCheckEnabled,
    sharedCoaches: draft.sharedCoaches?.map((coach) => coach.user.id),
    obtainableOnlyInBundle: draft.obtainableOnlyInBundle,
  }
}

export function convertPartialProgramInfiniteDraftToDto(
  draft: Partial<ProgramInfiniteDraft>,
): Partial<ProgramInfiniteDraftDto> {
  return {
    name: draft.name ?? undefined,
    description: draft.description ?? undefined,
    visibility: draft.visibility ?? undefined,
    image: draft.image?.path,
    defaultHideSessionContent: draft.defaultHideSessionContent ?? undefined,
    scoreboardVisibility: !draft.hideScoreboard,
    entranceCheckEnabled: draft.entranceCheckEnabled ?? undefined,
    sharedCoaches: draft.sharedCoaches?.map((coach) => coach.user.id),
    obtainableOnlyInBundle: draft.obtainableOnlyInBundle ?? false,
  }
}

export type ProgramInfiniteStripeSubscriptionInfosDto = {
  subscriptionId: string
  subscriptionStatus: string
  periodEndDate?: string
  referencePrice?: PriceObjectDto
  paidPrice?: PriceObjectDto
  trial?: ProgramTrialSubscriptionDto
  payment?: ProgramPaymentDto
}

export function convertProgramInfiniteStripeSubscriptionInfosFromDto(
  id: string,
  dto: ProgramInfiniteStripeSubscriptionInfosDto,
): ProgramInfiniteStripeSubscriptionInfos {
  return {
    id,
    subscriptionId: dto.subscriptionId,
    subscriptionStatus: convertToEnum(
      ProgramSubscriptionStatus,
      dto.subscriptionStatus,
      ProgramSubscriptionStatus.INACTIVE,
    ),
    paidPrice: convertProgramPriceFromDto(dto.paidPrice),
    referencePrice: convertProgramPriceFromDto(dto.referencePrice),
    payment: convertProgramPaymentFromDto(dto.payment),
    endPeriodDate: dto.periodEndDate,
    trial: dto.trial,
  }
}
